/**
 * smsSegments — GSM-7 / UCS-2 SMS segment calculator, normalizer, and campaign
 * cost estimator (frontend ESM port).
 *
 * CANONICAL SOURCE: teamio-lambda-layer/dependencies_source_for_layer/smsSegments.js
 * (deployed to /opt/smsSegments). The body between the SHARED-BODY markers must stay
 * byte-identical to the layer copy — cypress/node-tests/sms-segments.test.js fails
 * if they drift. Full rationale + the 2026-07-02 Invisalign-® incident write-up
 * live in the layer copy's header; segment math there is pinned to Twilio's real
 * billing by teamio-lambda-layer/__tests__/smsSegments.test.js.
 *
 * ESM here / CJS there: webpack can't run CommonJS under src/ (runtime crash,
 * see PROJECT_NOTES) and node:test can't import ESM without "type":"module" —
 * so the two export styles must differ while the body stays shared.
 */

// === SHARED-BODY-START === (everything between these markers must be byte-identical
// to teamio-frontend/src/utils/smsSegments.js — a frontend node-test enforces it)
// GSM 03.38 basic character set (1 septet each).
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
// GSM 03.38 extension table (ESC-prefixed, 2 septets each).
const GSM_EXTENDED = '^{}\\[~]|€';

const GSM_BASIC_SET = new Set(GSM_BASIC);
const GSM_EXTENDED_SET = new Set(GSM_EXTENDED);

// Non-GSM characters we can safely transliterate to a GSM equivalent.
// Covers Twilio Smart Encoding's list PLUS the symbols Twilio skips (®, ™, ©
 // — verified absent from their published replacement list, 2026-07-03).
const REPLACEMENTS = {
  // trademark/registered/copyright — the incident characters
  '®': '(R)',   // ®
  '™': '(TM)',  // ™
  '©': '(C)',   // ©
  // single quotes / apostrophes / primes
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  '′': "'", '´': "'", 'ʼ': "'", '`': "'",
  // double quotes
  '“': '"', '”': '"', '„': '"', '‟': '"',
  '″': '"', '«': '"', '»': '"',
  // dashes / hyphens
  '‐': '-', '‑': '-', '‒': '-', '–': '-',
  '—': '-', '―': '-', '−': '-',
  // ellipsis
  '…': '...',
  // bullets
  '•': '-', '·': '-',
  // spaces (typographic -> plain)
  ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ',
  ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ',
  ' ': ' ', ' ': ' ', ' ': ' ', ' ': ' ', '　': ' ',
  // zero-width (strip entirely)
  '​': '', '‌': '', '‍': '', '⁠': '', '﻿': '',
  // fractions
  '½': '1/2', '¼': '1/4', '¾': '3/4', '⅓': '1/3', '⅔': '2/3',
  // ligatures
  'ﬁ': 'fi', 'ﬂ': 'fl', 'œ': 'oe', 'Œ': 'OE',
};

// Twilio's own component pricing (US long-code, what campaigns use), verified
// against Twilio's official pricing 2026-07-07 (twilio.com/en-us/sms/pricing/us,
// help.twilio.com/articles/223133567). Tracked as their own constants (not just
// folded into one opaque number) so the carrier-fee piece is auditable on its
// own — carrier passthrough rates are the part most likely to drift over time.
const TWILIO_BASE_COST_PER_SEGMENT_USD = 0.0083;
const CARRIER_FEE_PER_SEGMENT_USD = 0.0042; // blended long-code passthrough, measured 2026-07-02
const TWILIO_BASE_COST_PER_MMS_USD = 0.022;
const CARRIER_FEE_PER_MMS_USD = 0.009; // long-code passthrough
// Twilio fee on any message (SMS or MMS) that ends in status Failed — separate
// from the base send cost, applies today even though nothing currently reads it.
const FAILED_MESSAGE_PROCESSING_FEE_USD = 0.001;

// All-in INTERNAL cost per outbound US long-code segment.
// INTERNAL cost — never show this to customers.
const COST_PER_SEGMENT_USD = 0.0125; // = TWILIO_BASE_COST_PER_SEGMENT_USD + CARRIER_FEE_PER_SEGMENT_USD

// All-in INTERNAL cost per outbound US long-code MMS message. Twilio bills MMS
// per MESSAGE, not per segment, and one message covers up to 10 media files at
// this same flat rate (not per-image). Campaigns don't send MMS yet (2026-07-07)
// — defined now so the cost model doesn't need a redesign whenever that ships.
// INTERNAL cost — never show this to customers.
const INTERNAL_COST_PER_MMS_USD = 0.031; // = TWILIO_BASE_COST_PER_MMS_USD + CARRIER_FEE_PER_MMS_USD

// CUSTOMER-FACING overage rate: texts beyond the plan's included monthly
// allowance bill at this rate PER SEGMENT (1 credit = 1 segment, industry
// standard). $0.025 ≈ 100% markup over the $0.0125 measured internal cost
// (~50% gross margin), still well under retail SMS platforms ($0.03-0.05).
// Display/estimate only for now — automatic billing is intentionally NOT wired up.
const OVERAGE_PRICE_PER_SEGMENT_USD = 0.025;

// A picture message (MMS) is folded into the SAME "credit" currency as texts
// rather than priced as a separate line — ONE unified balance / allowance /
// wallet, no second billing unit. Decided 2026-07-07 (Saumil): 1 picture
// message = 3 credits (= 3 × OVERAGE_PRICE_PER_SEGMENT_USD = $0.075). Integer
// credits chosen over the exact $0.05 target for simplicity, since MMS volume
// is tiny (~1-2 per site per month, per the Allay audit). Internal cost is
// INTERNAL_COST_PER_MMS_USD ($0.031) → ~59% margin. Twilio bills MMS per
// message (≤10 media flat), so the credit cost is flat regardless of caption
// length or image count.
const MMS_CREDITS = 3;

// PUERTO RICO pricing. A Puerto Rico number is a US/NANP number (country code +1) whose area code is
// 787 or 939 — both overlay codes for the same territory — so it never looks different to a US sender,
// but Twilio charges materially more to deliver to PR carriers. CUSTOMER-FACING: one SMS SEGMENT to a
// PR number spends PR_SEGMENT_CREDITS credits (= $0.125 at OVERAGE_PRICE_PER_SEGMENT_USD), not 1. That
// is the SAME 100%-markup-over-loaded-cost structure the US rate uses ($0.025 ≈ 2× the $0.0125 US
// internal cost), applied over the loaded PR cost below. A picture message (MMS) to PR is intentionally
// UNCHANGED at MMS_CREDITS — Twilio's PR MMS surcharge isn't characterized yet, MMS is flat-priced per
// message, and PR MMS volume is ~nil; revisit if PR MMS volume appears.
const PR_SEGMENT_CREDITS = 5;

// All-in INTERNAL loaded cost per outbound SMS segment to a Puerto Rico number: $0.054 Twilio base
// (verified via the Twilio Pricing API 2026-07-21 — flat across every PR carrier) + ~$0.004 blended
// carrier-fee estimate. INTERNAL cost — never show this to customers.
const PR_COST_PER_SEGMENT_USD = 0.058;

// NANP area codes that route to Puerto Rico (both are overlays for the same territory).
const PR_AREA_CODES = new Set(['787', '939']);

// CANADA — INTERNAL cost only (option A, Saumil 2026-09-15). A Canadian number is a +1/NANP number, so
// it looks like a US number to a sender, but Twilio charges more to deliver to Canadian carriers:
// Twilio base $0.0083 + Canadian carrier pass-through fee ~$0.0078 = $0.0161 per SMS segment. Measured
// from Smile Bar's Aug 2026 Twilio usage records + the Twilio Pricing API; Twilio support confirmed the
// Canadian carrier pass-through 2026-09-15. CUSTOMER pricing does NOT change: a segment to Canada still
// bills 1 credit (unlike PR). This constant feeds ONLY Teamio's internal cost estimate
// (estCostToTeamioUsd / estCostUsd). INTERNAL cost — never show this to customers.
const CA_COST_PER_SEGMENT_USD = 0.0161;

// Every Canadian NANP area code — geographic (in service + announced overlays) AND Canadian
// non-geographic (600 + the 6YY codes). Source: Canadian Numbering Administrator (CNA),
// cnac.ca/co_codes/co_code_status.htm (geographic NPAs) and
// cnac.ca/other_codes/nongeo/nongeo_code_status.htm + CRTC 2025-224 (600 / 622 / 633, with 644 / 655 /
// 677 / 688 reserved for Canada), read 2026-09-15. Announced overlays not yet dialable (e.g. 273, 428)
// are included on purpose: they can only ever be Canadian, so listing them early is safe.
const CA_AREA_CODES = new Set([
  // Alberta
  '403', '587', '780', '825', '368',
  // British Columbia
  '236', '250', '257', '604', '672', '778',
  // Manitoba
  '204', '431', '584',
  // New Brunswick
  '428', '506',
  // Newfoundland and Labrador
  '709', '879',
  // Nova Scotia / Prince Edward Island
  '782', '902',
  // Ontario
  '226', '249', '273', '289', '343', '365', '382', '416', '437', '519', '548', '613', '647', '683',
  '705', '742', '753', '807', '905', '942',
  // Quebec
  '263', '354', '367', '418', '438', '450', '468', '514', '579', '581', '819', '873',
  // Saskatchewan
  '306', '474', '639',
  // Northwest Territories / Nunavut / Yukon
  '867',
  // Canadian non-geographic
  '600', '622', '633', '644', '655', '677', '688',
]);

// ── WhatsApp pricing (2026-08-06, Saumil) ── WhatsApp is NOT segment-billed: Twilio charges per
// MESSAGE (Meta moved to per-message billing on 2025-07-01). So it folds into the SAME unified
// "credit" currency as SMS/MMS at a FLAT rate — decided FLAT 1 credit per WhatsApp message, drawn
// from the shared SMS/messaging pool (exactly like an MMS is a flat MMS_CREDITS). Tunable here
// without touching the engine. The two INTERNAL cost numbers below are only the send-time ESTIMATE
// written to the ledger — the ACTUAL Twilio price is backfilled onto the sms_send_log row from the
// delivery status callback (captureActualCostBestEffort reads msg.price for WhatsApp too):
//   - in-window (free-form reply, within the 24h service window): ~Twilio carrier fee only; Meta $0.
//   - out-of-window (approved template, business-initiated): Twilio + Meta's category fee.
// CUSTOMER charge is WHATSAPP_CREDITS; the cost numbers are INTERNAL — never show them to customers.
const WHATSAPP_CREDITS = 1;

// ── Meta's rate card (2026-09-21) ── Meta prices by the RECIPIENT's market, not the sender's:
// "Meta's … fee is based on your customer's WhatsApp country code". Figures below are Meta's
// published "Cost per message in USD on the WhatsApp Business Platform, effective July 1, 2026"
// (the USD rates CSV linked from developers.facebook.com/docs/whatsapp/pricing):
//
//     Market          Marketing   Utility   Authentication
//     North America   0.0250      0.0034    0.0034
//     Other           0.0604      0.0077    0.0077
//
// Meta's market table lists ONLY Canada and United States under "North America" and states
// "If a country is not listed below, it maps to Other." So every OTHER NANP destination is
// "Other" DESPITE the shared +1 — Barbados (246), Jamaica, the Bahamas, Trinidad, the Dominican
// Republic, Puerto Rico. This is why buying a practice a US From number does not make its
// Barbados patients cheap: both Twilio and Meta price on the recipient.
const WHATSAPP_TWILIO_FEE_USD = 0.005;          // Twilio's per-message channel fee, either direction
const WHATSAPP_META_UTILITY_NA_USD = 0.0034;    // Meta Utility/Authentication — North America
const WHATSAPP_META_UTILITY_OTHER_USD = 0.0077; // Meta Utility/Authentication — "Other" (e.g. Barbados)
const WHATSAPP_META_MARKETING_NA_USD = 0.0250;  // Meta Marketing — North America
const WHATSAPP_META_MARKETING_OTHER_USD = 0.0604; // Meta Marketing — "Other"
//
// ⚠️ MARKETING is NOT represented in the cost constants below, deliberately. At 1 credit
// ($0.025 revenue) a marketing template LOSES money in both markets — $0.0654 to "Other",
// $0.0300 even in North America. We are not exposed: waOnboarding/templates.js pins
// `TEMPLATE_CATEGORY = 'UTILITY'` ("UTILITY is the only one an appointment reminder may use").
// If a MARKETING template is ever introduced, WHATSAPP_CREDITS must be revisited FIRST — the
// margin guard in __tests__/smsSegments-whatsapp-market.test.js exists to make that unmissable.
//
// 2026-10-01 — Meta begins charging per-message for SERVICE messages (free since Nov 2024) AND
// for utility templates sent INSIDE the open 24-hour customer service window, at the utility
// rate, with "no volume tiers for service messages". The in-window constants therefore carry
// Meta's utility fee, not the bare Twilio fee as they did before. These values are applied from
// now rather than switched on the date: they are send-time ESTIMATES only (the ACTUAL Twilio
// price is backfilled onto sms_send_log by captureActualCostBestEffort), and the pre-Oct-1 error
// is ~$0.003/message in the CONSERVATIVE direction — it understates margin rather than
// flattering it, and it leaves no dated landmine to deploy through.
const WHATSAPP_INWINDOW_COST_USD = WHATSAPP_TWILIO_FEE_USD + WHATSAPP_META_UTILITY_NA_USD;          // 0.0084
const WHATSAPP_INWINDOW_COST_OTHER_USD = WHATSAPP_TWILIO_FEE_USD + WHATSAPP_META_UTILITY_OTHER_USD; // 0.0127
const WHATSAPP_TEMPLATE_COST_USD = WHATSAPP_TWILIO_FEE_USD + WHATSAPP_META_UTILITY_NA_USD;          // 0.0084
const WHATSAPP_TEMPLATE_COST_OTHER_USD = WHATSAPP_TWILIO_FEE_USD + WHATSAPP_META_UTILITY_OTHER_USD; // 0.0127

// NANP area codes that share country code 1 but are NOT Meta's "North America" market (which is
// Canada + the United States only). Sourced from Twilio's own Messaging geo-permissions country
// list, which enumerates each NANP country with its +1 prefix. Puerto Rico and the other US
// territories are included DELIBERATELY: Meta's table lists "United States / US", and PR/VI/GU/AS/MP
// each carry their own ISO country code. If that reading is wrong the effect is to OVERSTATE our
// cost — which understates margin, the safe direction for an estimate.
const NANP_NON_NORTH_AMERICA_AREA_CODES = new Set([
  '242', // Bahamas
  '246', // Barbados
  '264', // Anguilla
  '268', // Antigua and Barbuda
  '284', // Virgin Islands, British
  '340', // Virgin Islands, U.S.
  '345', // Cayman Islands
  '441', // Bermuda
  '473', // Grenada
  '649', // Turks and Caicos Islands
  '658', '876', // Jamaica
  '664', // Montserrat
  '670', // Northern Mariana Islands
  '671', // Guam
  '684', // American Samoa
  '721', // Sint Maarten
  '758', // St Lucia
  '767', // Dominica
  '784', // St Vincent and the Grenadines
  '787', '939', // Puerto Rico
  '809', '829', '849', // Dominican Republic
  '868', // Trinidad and Tobago
  '869', // St Kitts and Nevis
]);

// Stripe rejects any charge under $0.50 USD ("Amount must convert to at least 50
// cents"). A genuine overage below this (fewer than 20 chargeable segments at
// $0.025) would otherwise fail the charge and BLOCK the whole campaign over a few
// cents. Policy (Saumil 2026-07-04): round the charge UP to this floor. The raw
// per-segment math (overageCostUsd) is kept for the honest breakdown; the amount
// we actually bill (and the customer consents to) is chargeUsd.
const STRIPE_MIN_CHARGE_USD = 0.50;

// US mobile carriers reject concatenated long-code SMS beyond ~10 segments
// (Twilio error 30019 "Content size exceeds carrier limit") and still bill for
// the rejected attempt. Proven 2026-07-02: all 978 of Allay's undelivered
// messages were the ®-inflated 12-segment campaign texts (≈$146 billed, zero
// delivered). Messages above this cap are HARD-BLOCKED before send — not
// confirmable and never charged, because delivery is impossible by construction.
const CARRIER_MAX_SEGMENTS_PER_MESSAGE = 10;

const GSM_SINGLE = 160;   // GSM-7 septets in an unsegmented SMS
const GSM_MULTI = 153;    // per segment once concatenated (UDH header overhead)
const UCS2_SINGLE = 70;   // UTF-16 code units in an unsegmented SMS
const UCS2_MULTI = 67;    // per segment once concatenated

function isGsmChar(ch) {
  return GSM_BASIC_SET.has(ch) || GSM_EXTENDED_SET.has(ch);
}

/**
 * Analyze one message body exactly as Twilio will bill it.
 * @param {string} text
 * @returns {{encoding:'GSM-7'|'UCS-2', segments:number, length:number,
 *   septets:number|null, charsPerSegment:number,
 *   nonGsmChars:Array<{char:string, codePoint:number, count:number, replacement:string|null}>}}
 */
function analyzeSms(text) {
  const s = text == null ? '' : String(text);

  let septets = 0;
  let allGsm = true;
  const offenders = new Map(); // char -> count (iterated by code point so emoji stay whole)
  for (const ch of s) {
    if (GSM_BASIC_SET.has(ch)) {
      septets += 1;
    } else if (GSM_EXTENDED_SET.has(ch)) {
      septets += 2;
    } else {
      allGsm = false;
      offenders.set(ch, (offenders.get(ch) || 0) + 1);
    }
  }

  const nonGsmChars = [...offenders.entries()].map(([char, count]) => ({
    char,
    codePoint: char.codePointAt(0),
    count,
    replacement: Object.prototype.hasOwnProperty.call(REPLACEMENTS, char)
      ? REPLACEMENTS[char]
      : null,
  }));

  if (allGsm) {
    const segments = septets <= GSM_SINGLE ? 1 : Math.ceil(septets / GSM_MULTI);
    return {
      encoding: 'GSM-7',
      segments,
      length: s.length,
      septets,
      charsPerSegment: septets <= GSM_SINGLE ? GSM_SINGLE : GSM_MULTI,
      nonGsmChars,
    };
  }

  // UCS-2 bills per UTF-16 code unit (astral chars = 2), which is s.length.
  const units = s.length;
  const segments = units <= UCS2_SINGLE ? 1 : Math.ceil(units / UCS2_MULTI);
  return {
    encoding: 'UCS-2',
    segments,
    length: units,
    septets: null,
    charsPerSegment: units <= UCS2_SINGLE ? UCS2_SINGLE : UCS2_MULTI,
    nonGsmChars,
  };
}

/**
 * Replace every transliterable non-GSM character with its GSM equivalent.
 * Characters with no safe equivalent (emoji, etc.) are left in place and
 * reported in `unreplaceable` — if any remain, the message still bills as UCS-2.
 * @param {string} text
 * @returns {{text:string, changed:boolean,
 *   replacements:Array<{from:string, to:string, count:number}>,
 *   unreplaceable:string[]}}
 */
function normalizeSms(text) {
  const s = text == null ? '' : String(text);
  const replaced = new Map();   // from -> count
  const unreplaceable = new Set();
  let out = '';

  for (const ch of s) {
    if (isGsmChar(ch)) {
      out += ch;
    } else if (Object.prototype.hasOwnProperty.call(REPLACEMENTS, ch)) {
      out += REPLACEMENTS[ch];
      replaced.set(ch, (replaced.get(ch) || 0) + 1);
    } else {
      out += ch;
      unreplaceable.add(ch);
    }
  }

  return {
    text: out,
    changed: replaced.size > 0,
    replacements: [...replaced.entries()].map(([from, count]) => ({
      from,
      to: REPLACEMENTS[from],
      count,
    })),
    unreplaceable: [...unreplaceable],
  };
}

// Conservative sample values for merge tags when estimating from a template.
// Slightly long on purpose: a gate should over-estimate, not under-estimate.
const SAMPLE_VARS = {
  patient_first_name: 'Christopher',
  patient_last_name: 'Buttersworth',
  patient_name: 'Christopher Buttersworth',
  site_name: 'Allay Dental Group',
  site_officephone: '(919) 578-3069',
};
const DEFAULT_SAMPLE = 'Xxxxxxxxxxxx'; // 12 chars for unknown tags

function substituteTags(template, vars) {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key) => {
    if (vars && Object.prototype.hasOwnProperty.call(vars, key)) return String(vars[key]);
    if (Object.prototype.hasOwnProperty.call(SAMPLE_VARS, key)) return SAMPLE_VARS[key];
    return DEFAULT_SAMPLE;
  });
}

/**
 * Estimate the segment/cost footprint of sending `text` to `recipientCount`
 * patients. Merge tags ({{patient_first_name}} etc.) are substituted with
 * conservative sample values unless real values are passed in opts.vars.
 *
 * opts.mode:
 *  - 'optimized' (default): totals assume we send the normalized text
 *  - 'original': customer chose to keep formatting as typed — totals use the
 *    as-typed encoding and savings are 0
 *
 * opts.prRecipientCount (default 0): how many of `recipientCount` route to Puerto Rico. Those
 * recipients bill PR_SEGMENT_CREDITS per segment and cost Teamio PR_COST_PER_SEGMENT_USD per segment;
 * the rest bill 1 credit / COST_PER_SEGMENT_USD. `totalCredits` is the CUSTOMER unit (what the wallet
 * draws / the plan allowance is spent in); `totalSegments` stays the PHYSICAL Twilio segment footprint
 * (carrier/volume math). Guarded to [0, recipients]. prRecipientCount 0 → totalCredits === totalSegments
 * and estCostUsd is byte-identical to the pre-PR value.
 *
 * opts.caRecipientCount (default 0): how many of `recipientCount` route to CANADA. It changes ONLY
 * estCostUsd (those segments cost Teamio CA_COST_PER_SEGMENT_USD) — Canadian recipients bill 1 credit
 * per segment like US ones, so totalCredits is untouched. Guarded to [0, recipients − prRecipients].
 * caRecipientCount 0 → every output value is identical to the pre-Canada result.
 *
 * estCostUsd is Teamio's INTERNAL cost (COST_PER_SEGMENT_USD / PR_COST_PER_SEGMENT_USD /
 * CA_COST_PER_SEGMENT_USD) — for audit logs and admin dashboards, never for customer display.
 */
function estimateCampaignSms(text, recipientCount, opts = {}) {
  const mode = opts.mode === 'original' ? 'original' : 'optimized';
  const recipients = Math.max(0, parseInt(recipientCount, 10) || 0);
  const prRecipients = Math.min(recipients, Math.max(0, parseInt(opts.prRecipientCount, 10) || 0));
  const caRecipients = Math.min(recipients - prRecipients, Math.max(0, parseInt(opts.caRecipientCount, 10) || 0));
  const rendered = substituteTags(text == null ? '' : String(text), opts.vars);

  const asTyped = analyzeSms(rendered);
  const norm = normalizeSms(rendered);
  const normalizedAnalysis = analyzeSms(norm.text);

  const perRecipientSegments =
    mode === 'original' ? asTyped.segments : normalizedAnalysis.segments;
  const totalSegments = perRecipientSegments * recipients;
  const savingsSegments =
    mode === 'original'
      ? 0
      : Math.max(0, asTyped.segments - normalizedAnalysis.segments) * recipients;

  // CUSTOMER credits: PR recipients spend PR_SEGMENT_CREDITS/segment, the rest spend 1. Written as
  // (totalSegments − prSegments)·1 + prSegments·PR so prRecipients 0 reduces EXACTLY to totalSegments.
  const prSegments = perRecipientSegments * prRecipients;
  const totalCredits = (totalSegments - prSegments) + prSegments * PR_SEGMENT_CREDITS;
  // Canadian segments: INTERNAL cost only (never credits).
  const caSegments = perRecipientSegments * caRecipients;

  return {
    mode,
    recipients,
    prRecipients,
    caRecipients,
    asTyped: { segments: asTyped.segments, encoding: asTyped.encoding },
    normalized: {
      segments: normalizedAnalysis.segments,
      encoding: normalizedAnalysis.encoding,
      text: norm.text,
      changed: norm.changed,
    },
    unreplaceable: norm.unreplaceable,
    perRecipientSegments,
    totalSegments,
    totalCredits,
    savingsSegments,
    // (nonPR-nonCA·US + PR·PR + CA·CA) split; with prSegments 0 and caSegments 0 this is exactly
    // totalSegments * COST_PER_SEGMENT_USD. The CA term is only added when present so a caRecipientCount
    // of 0 keeps the value byte-identical to the pre-Canada formula (no float drift from adding 0·x).
    estCostUsd: caSegments > 0
      ? (totalSegments - prSegments - caSegments) * COST_PER_SEGMENT_USD
        + prSegments * PR_COST_PER_SEGMENT_USD + caSegments * CA_COST_PER_SEGMENT_USD
      : (totalSegments - prSegments) * COST_PER_SEGMENT_USD + prSegments * PR_COST_PER_SEGMENT_USD,
  };
}

/**
 * Customer-facing overage dollars for a campaign against the site's monthly
 * SEGMENT allowance. Texts bill per segment (Twilio bills Teamio per segment),
 * so the allowance is counted in segments too: a text up to 160 GSM-7 chars is
 * one segment; longer texts spend one segment per ~153 chars. Only the
 * CAMPAIGN's segments that land beyond the allowance are chargeable — segments
 * already consumed earlier this month are never re-attributed to this send.
 * Returns whole-campaign totals at OVERAGE_PRICE_PER_SEGMENT_USD.
 *
 * Counting the allowance in messages (not segments) while paying Twilio per
 * segment silently ate Teamio's margin on long texts (2026-07-03 review): a
 * 5-segment blast to 3,000 patients that "fit" a 5,000-message plan billed the
 * customer $0 while costing Teamio ~$187. Segment units close that gap.
 *
 * @param {Object} p
 * @param {number} p.segmentsUsed     segments already sent this month (all traffic)
 * @param {number} p.allowance        plan's included SEGMENTS per month (e.g. 5000)
 * @param {number} p.campaignSegments total segments this campaign will send
 * `overageCostUsd` is the raw per-segment math (for the breakdown); `chargeUsd` is
 * what we actually bill — the same value, but floored to Stripe's STRIPE_MIN_CHARGE_USD
 * minimum when there IS an overage (so a sub-$0.50 overage doesn't fail the charge and
 * block the send). `minimumApplied` flags when the floor bumped the amount so the UI can
 * disclose it. No overage → chargeUsd 0, minimumApplied false (we never charge $0.50 for $0).
 *
 * @returns {{projectedSegments:number, chargeableSegments:number, overageCostUsd:number, chargeUsd:number, minimumApplied:boolean}}
 */
function estimateOverageCharge(p) {
  const used = Math.max(0, parseInt(p && p.segmentsUsed, 10) || 0);
  const allowance = Math.max(0, parseInt(p && p.allowance, 10) || 0);
  const campaignSegments = Math.max(0, parseInt(p && p.campaignSegments, 10) || 0);

  const projectedSegments = used + campaignSegments;
  const chargeableSegments = Math.min(campaignSegments, Math.max(0, projectedSegments - allowance));

  const overageCostUsd = +(chargeableSegments * OVERAGE_PRICE_PER_SEGMENT_USD).toFixed(2);
  const chargeUsd = overageCostUsd > 0 ? Math.max(overageCostUsd, STRIPE_MIN_CHARGE_USD) : 0;
  const minimumApplied = chargeUsd > overageCostUsd;

  return {
    projectedSegments,
    chargeableSegments,
    overageCostUsd,
    chargeUsd,
    minimumApplied,
  };
}

/**
 * True iff `phone` is a US/NANP number that routes to PUERTO RICO (area code 787 or 939). Tolerant of
 * every shape we store or receive: E.164 ('+17873569589'), 11-digit with country code ('17873569589'),
 * or bare national 10-digit ('7873569589' — the digits-only form campaign recipients carry). Anything
 * that is not a NANP number (wrong digit count, a +44… international number, empty/null) → false, so a
 * non-US destination is never mis-priced as PR. Pure.
 * @param {string} phone
 * @returns {boolean}
 */
function isPuertoRicoDestination(phone) {
  const npa = nanpAreaCode(phone);
  return npa !== null && PR_AREA_CODES.has(npa);
}

/**
 * True iff `phone` is a NANP number whose area code is Canadian (CA_AREA_CODES). Same parsing as
 * isPuertoRicoDestination (E.164, 11-digit with country code, bare 10-digit, formatted); a non-NANP or
 * empty input → false. Used for Teamio's INTERNAL cost estimate only — never for customer credits. Pure.
 * @param {string} phone
 * @returns {boolean}
 */
function isCanadaDestination(phone) {
  const npa = nanpAreaCode(phone);
  return npa !== null && CA_AREA_CODES.has(npa);
}

/**
 * True iff `phone` is in Meta's "North America" WhatsApp market — which Meta defines as Canada and
 * the United States ONLY. A NANP number qualifies unless its area code is in
 * NANP_NON_NORTH_AMERICA_AREA_CODES (Barbados, Jamaica, Puerto Rico, the rest of the Caribbean and
 * the US territories); anything non-NANP (+44…, +52…) is "Other". Same tolerant parsing as the PR
 * and Canada checks — E.164, 11-digit, bare 10-digit, formatted — and junk/null → false, i.e.
 * "Other", the dearer assumption.
 *
 * INTERNAL cost estimation only. It must NEVER influence billableCredits: the customer pays a flat
 * WHATSAPP_CREDITS whatever the destination. Pure.
 * @param {string} phone
 * @returns {boolean}
 */
function isNorthAmericaWhatsAppDestination(phone) {
  const npa = nanpAreaCode(phone);
  return npa !== null && !NANP_NON_NORTH_AMERICA_AREA_CODES.has(npa);
}

// The 3-digit NANP area code of `phone`, or null when it is not a NANP number (wrong digit count,
// international, empty/null). Shared by the PR and Canada destination checks so they parse identically.
function nanpAreaCode(phone) {
  if (phone == null) return null;
  const digits = String(phone).replace(/\D/g, '');
  let national;
  if (digits.length === 11 && digits[0] === '1') national = digits.slice(1);
  else if (digits.length === 10) national = digits;
  else return null; // not a NANP number
  return national.slice(0, 3);
}

/**
 * The single pre-send rule check every outbound message runs through. Composes
 * normalize + analyze + carrier-cap + internal-cost into one pure result so both
 * smsEngine (backend) and the composer (frontend) enforce IDENTICAL rules.
 *
 * @param {string} body
 * @param {{media?:Array, mediaCount?:number, smsFormatting?:'original'|'optimized', toPhone?:string}} [opts]
 *   opts.toPhone — the destination number. When it routes to Puerto Rico (isPuertoRicoDestination),
 *   an SMS bills PR_SEGMENT_CREDITS per segment and its internal cost uses PR_COST_PER_SEGMENT_USD.
 *   Omitted (e.g. an inbound message we RECEIVED, or a legacy caller) → US pricing, byte-identical to
 *   before. MMS is destination-independent (always MMS_CREDITS / INTERNAL_COST_PER_MMS_USD).
 *   opts.mediaCount — how many media items the CARRIER handled (Twilio's NumMedia), used when the
 *   caller's `media` array may be incomplete. Inbound metering passes the post-S3-re-host `files`
 *   as `media`, and that re-host is allowed to fail (a broken photo must not drop the patient's
 *   message) — so on failure `media` is [] for a message Twilio billed us as an MMS. Classifying
 *   off `media` alone therefore under-billed it as a 1-segment SMS. Either signal makes it an MMS.
 * @returns {{finalBody:string, wasNormalized:boolean, encoding:string,
 *   segments:number, messageType:'sms'|'mms', billableCredits:number, estCostToTeamioUsd:number,
 *   blocked:boolean, blockedReason:string|null}}
 */
function evaluateOutboundMessage(body, opts = {}) {
  const isMms = (Array.isArray(opts.media) && opts.media.length > 0) || Number(opts.mediaCount) > 0;

  let finalBody = body == null ? '' : String(body);

  // TRAILING WHITESPACE IS NOT BILLABLE. Twilio discards it before it counts segments, so any
  // trailing space/newline we keep is a septet we charge for and Twilio does not.
  //
  // Measured on prod 2026-07-20: two form-link SMS were metered at 2 segments while Twilio billed
  // 1. Both were exactly 159 septets of real text plus a trailing " \n" — 161, one septet past the
  // 160 single-segment limit. The source is teamio-utility's processLink, whose anchor->text
  // replacement appends a trailing space while the composer's own trailing newline survives its
  // regex. Only 2 of 1,715 messages crossed the boundary, but EVERY link-bearing message was being
  // counted 2 septets high; the boundary is just where it became visible as a charge.
  //
  // Trimming here rather than in processLink fixes it for every caller and keeps our segment count
  // equal to the one Twilio bills. It is applied unconditionally — including smsFormatting
  // 'original' — because it cannot change the delivered message: Twilio strips it either way. Only
  // TRAILING is touched; leading whitespace is left alone since it was never shown to be discarded.
  finalBody = finalBody.replace(/\s+$/, '');

  let wasNormalized = false;
  if (opts.smsFormatting !== 'original') {
    const norm = normalizeSms(finalBody);
    finalBody = norm.text;
    wasNormalized = norm.changed;
  }

  const analysis = analyzeSms(finalBody);
  const messageType = isMms ? 'mms' : 'sms';

  // The 10-segment carrier cap (error 30019) is a long-code SMS limit; MMS bills
  // per-message and is not segmented that way, so the cap does not apply to MMS.
  const blocked = messageType === 'sms' && analysis.segments > CARRIER_MAX_SEGMENTS_PER_MESSAGE;

  // Puerto Rico surcharge applies to SMS only (MMS is flat per-message and destination-independent).
  // A PR SMS segment costs Teamio PR_COST_PER_SEGMENT_USD (vs COST_PER_SEGMENT_USD) and bills the
  // customer PR_SEGMENT_CREDITS per segment (vs 1).
  const isPr = !isMms && isPuertoRicoDestination(opts.toPhone);
  // Canada: INTERNAL cost only — a Canadian SMS segment costs Teamio CA_COST_PER_SEGMENT_USD but bills
  // the customer exactly like a US segment (billableCredits below never looks at isCa). PR wins first.
  const isCa = !isMms && !isPr && isCanadaDestination(opts.toPhone);

  const smsCostPerSegment = isPr ? PR_COST_PER_SEGMENT_USD : (isCa ? CA_COST_PER_SEGMENT_USD : COST_PER_SEGMENT_USD);
  const estCostToTeamioUsd = isMms
    ? INTERNAL_COST_PER_MMS_USD
    : +(analysis.segments * smsCostPerSegment).toFixed(6);

  // What the CUSTOMER is billed, in the unified credit currency: a US SMS spends one credit per
  // segment; a Puerto Rico SMS spends PR_SEGMENT_CREDITS per segment; an MMS spends a flat MMS_CREDITS
  // regardless of caption length, image count, or destination (Twilio bills MMS per message).
  const billableCredits = isMms
    ? MMS_CREDITS
    : analysis.segments * (isPr ? PR_SEGMENT_CREDITS : 1);

  return {
    finalBody,
    wasNormalized,
    encoding: analysis.encoding,
    segments: analysis.segments,
    messageType,
    billableCredits,
    estCostToTeamioUsd,
    blocked,
    blockedReason: blocked ? 'carrier_segment_cap' : null,
  };
}

/**
 * WhatsApp cost/credit rules — the WhatsApp twin of evaluateOutboundMessage. WhatsApp is NOT
 * segment-billed (Twilio charges per MESSAGE), so there is no encoding/segment analysis and no
 * carrier-segment-cap block: a send is a FLAT WHATSAPP_CREDITS. Returns the SAME shape as
 * evaluateOutboundMessage so the engine's shared wallet-draw + send-log + actual-cost-capture path
 * runs for WhatsApp unchanged (before this, WhatsApp bypassed all of it — drew nothing, logged
 * nothing: the credit leak fixed 2026-08-06). `finalBody` is not meaningful for WhatsApp (the engine
 * sends the caller's raw body); `estCostToTeamioUsd` is an ESTIMATE only — the real Twilio price is
 * backfilled onto the row from the status callback. A template (contentSid → business-initiated,
 * out-of-window) carries the Meta category fee; a free-form in-window reply is the Twilio fee only.
 * @param {{contentSid?:string}} opts
 * @returns {{finalBody:null, wasNormalized:boolean, encoding:null, segments:number,
 *   messageType:'whatsapp', billableCredits:number, estCostToTeamioUsd:number, blocked:boolean,
 *   blockedReason:null}}
 */
function evaluateOutboundWhatsApp(opts = {}) {
  const isTemplate = !!(opts && opts.contentSid);
  // Meta prices per the RECIPIENT's market, so the estimate follows `toPhone` exactly as the SMS
  // path follows it for Puerto Rico and Canada. When no destination is supplied we assume North
  // America: that is the documented default for the overwhelmingly US fleet, and the engine always
  // supplies one — pinned by the SOURCE guard in smsSegments-whatsapp-market.test.js, without which
  // this branch would be dead code.
  const isNorthAmerica = opts && opts.toPhone !== undefined && opts.toPhone !== null
    ? isNorthAmericaWhatsAppDestination(opts.toPhone)
    : true;
  const estCostToTeamioUsd = isTemplate
    ? (isNorthAmerica ? WHATSAPP_TEMPLATE_COST_USD : WHATSAPP_TEMPLATE_COST_OTHER_USD)
    : (isNorthAmerica ? WHATSAPP_INWINDOW_COST_USD : WHATSAPP_INWINDOW_COST_OTHER_USD);
  return {
    finalBody: null,
    wasNormalized: false,
    encoding: null,
    segments: 1, // one message unit — counts as 1 credit toward the shared free allowance
    messageType: 'whatsapp',
    // FLAT regardless of destination — the market changes Teamio's COST, never the customer's price.
    billableCredits: WHATSAPP_CREDITS,
    estCostToTeamioUsd,
    blocked: false,
    blockedReason: null,
  };
}

// === SHARED-BODY-END ===

export {
  analyzeSms,
  normalizeSms,
  estimateCampaignSms,
  estimateOverageCharge,
  evaluateOutboundMessage,
  isPuertoRicoDestination,
  isCanadaDestination,
  substituteTags,
  COST_PER_SEGMENT_USD,
  OVERAGE_PRICE_PER_SEGMENT_USD,
  STRIPE_MIN_CHARGE_USD,
  CARRIER_MAX_SEGMENTS_PER_MESSAGE,
  TWILIO_BASE_COST_PER_SEGMENT_USD,
  CARRIER_FEE_PER_SEGMENT_USD,
  TWILIO_BASE_COST_PER_MMS_USD,
  CARRIER_FEE_PER_MMS_USD,
  FAILED_MESSAGE_PROCESSING_FEE_USD,
  INTERNAL_COST_PER_MMS_USD,
  MMS_CREDITS,
  PR_SEGMENT_CREDITS,
  PR_COST_PER_SEGMENT_USD,
  PR_AREA_CODES,
  CA_COST_PER_SEGMENT_USD,
  CA_AREA_CODES,
  GSM_BASIC,
  GSM_EXTENDED,
  REPLACEMENTS,
};
